import { useCallback, useEffect, useState } from 'react';
import { loadConfig, type AppConfig } from './config.js';
import { apiFetch } from './api/client.js';
import { loadTokens, signOut } from './auth/session.js';
import { SignIn } from './screens/SignIn.js';
import { Today } from './screens/Today.js';
import './ui/styles.css';

interface Me {
  userId: string;
  displayName?: string;
  groups: string[];
  patients: AccessiblePatient[];
}

interface AccessiblePatient {
  patientId: string;
  displayName: string;
  role: 'SELF' | 'CAREGIVER';
  permissions: string[];
  isTest: boolean;
}

type Phase =
  | { kind: 'loading' }
  | { kind: 'config-error'; message: string }
  | { kind: 'signed-out'; config: AppConfig }
  | { kind: 'ready'; config: AppConfig; me: Me; patientId: string };

export function App(): React.JSX.Element {
  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });

  const start = useCallback(async () => {
    let config: AppConfig;
    try {
      config = await loadConfig();
    } catch (e) {
      setPhase({ kind: 'config-error', message: e instanceof Error ? e.message : String(e) });
      return;
    }

    if (!(await loadTokens())) {
      setPhase({ kind: 'signed-out', config });
      return;
    }

    try {
      const me = await apiFetch<Me>(config, '/v1/me');
      // A patient lands on their own log; a caregiver lands on the person they care for.
      const own = me.patients.find((p) => p.role === 'SELF');
      // Prefer a real patient over a test one, so a caregiver never lands on test
      // data by accident and mistakes it for a real chart.
      const firstReal = me.patients.find((p) => !p.isTest);
      const patientId = own?.patientId ?? firstReal?.patientId ?? me.patients[0]?.patientId;
      if (patientId === undefined) {
        setPhase({ kind: 'config-error', message: 'This account is not linked to anyone yet.' });
        return;
      }
      setPhase({ kind: 'ready', config, me, patientId });
    } catch {
      // Could not confirm the session — treat as signed out rather than showing a
      // half-broken screen. Nothing local is discarded.
      setPhase({ kind: 'signed-out', config });
    }
  }, []);

  useEffect(() => {
    void start();
  }, [start]);

  switch (phase.kind) {
    case 'loading':
      return <main className="app"><p className="muted">Loading…</p></main>;
    case 'config-error':
      return (
        <main className="app">
          <h1>Med Log</h1>
          <p className="error">{phase.message}</p>
        </main>
      );
    case 'signed-out':
      return <SignIn config={phase.config} onSignedIn={() => void start()} />;
    case 'ready': {
      const current = phase.me.patients.find((p) => p.patientId === phase.patientId);
      return (
        <Today
          key={phase.patientId}
          config={phase.config}
          patientId={phase.patientId}
          readOnly={current?.role !== 'SELF'}
          patients={phase.me.patients}
          onSelectPatient={(patientId) => {
            setPhase({ ...phase, patientId });
          }}
          onSignOut={() => {
            void signOut().then(() => void start());
          }}
        />
      );
    }
  }
}
